import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/apiRouting';
import './BillingManagement.css';

interface SubscriptionInfo {
  planName: string;
  planTier: string;
  status: 'active' | 'trialing' | 'past_due' | 'cancelled' | 'paused';
  mrr: number;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  nextPaymentDate: string;
  locationCount: number;
}

interface BillingManagementProps {
  user: any;
}

const BillingManagement: React.FC<BillingManagementProps> = ({ user }) => {
  const { getAccessToken, session } = useAuth();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<SubscriptionInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      const payload = session?.getIdToken()?.payload;
      const storeId = payload?.['custom:enterprise_id'] || payload?.enterpriseId || '';

      if (!storeId) {
        setSubscription(null);
        setLoading(false);
        return;
      }

      const response = await fetch(
        `${API_ENDPOINTS.billingHealth()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        // Health endpoint may not return subscription data;
        // treat non-OK as no subscription found
        setSubscription(null);
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (data.subscription) {
        setSubscription({
          planName: data.subscription.planName || 'Unknown Plan',
          planTier: data.subscription.planTier || 'unknown',
          status: data.subscription.status || 'active',
          mrr: data.subscription.mrr || 0,
          currentPeriodStart: data.subscription.currentPeriodStart || '',
          currentPeriodEnd: data.subscription.currentPeriodEnd || '',
          nextPaymentDate: data.subscription.nextPaymentDate || '',
          locationCount: data.subscription.locationCount || 1,
        });
      } else {
        setSubscription(null);
      }
    } catch (err: any) {
      // If fetching fails, show no subscription rather than an error
      // since the endpoint may not support subscription retrieval yet
      setSubscription(null);
    } finally {
      setLoading(false);
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError(null);

    try {
      const token = await getAccessToken();
      if (!token) {
        setError('Authentication expired. Please log in again.');
        setPortalLoading(false);
        return;
      }

      const payload = session?.getIdToken()?.payload;
      const storeId = payload?.['custom:enterprise_id'] || payload?.enterpriseId || '';

      const response = await fetch(API_ENDPOINTS.billingPortal(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ storeId }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Failed to open billing portal (${response.status})`);
      }

      const data = await response.json();

      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No portal URL returned from server.');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to open billing portal. Please try again.');
    } finally {
      setPortalLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '--';
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '--';
    }
  };

  const getStatusLabel = (status: string): string => {
    const labels: Record<string, string> = {
      active: 'Active',
      trialing: 'Trial',
      past_due: 'Past Due',
      cancelled: 'Cancelled',
      paused: 'Paused',
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="billing-management">
        <div className="billing-loading">
          <div className="billing-spinner" />
          <p>Loading billing information...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="billing-management">
      <div className="billing-header">
        <h2 className="billing-title">Billing</h2>
        <p className="billing-subtitle">
          Manage your subscription, payment methods, and invoices.
        </p>
      </div>

      {error && (
        <div className="billing-error-banner">
          <span>{error}</span>
          <button
            className="billing-error-dismiss"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
          >
            X
          </button>
        </div>
      )}

      {subscription ? (
        <>
          <div className="billing-stats">
            <div className="stat-card">
              <h3>{subscription.planName}</h3>
              <p>Plan Tier</p>
            </div>
            <div className="stat-card">
              <h3>
                <span className={`status-badge ${subscription.status}`}>
                  {getStatusLabel(subscription.status)}
                </span>
              </h3>
              <p>Status</p>
            </div>
            <div className="stat-card">
              <h3>{formatCurrency(subscription.mrr)}</h3>
              <p>Monthly Recurring Revenue</p>
            </div>
            <div className="stat-card">
              <h3>{subscription.locationCount}</h3>
              <p>Locations</p>
            </div>
          </div>

          <div className="billing-summary-card">
            <h3 className="summary-title">Subscription Details</h3>
            <div className="summary-grid">
              <div className="summary-row">
                <span className="summary-label">Plan</span>
                <span className="summary-value">{subscription.planName}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Status</span>
                <span className="summary-value">
                  <span className={`status-badge ${subscription.status}`}>
                    {getStatusLabel(subscription.status)}
                  </span>
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Current Period</span>
                <span className="summary-value">
                  {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
                </span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Next Payment</span>
                <span className="summary-value">{formatDate(subscription.nextPaymentDate)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Locations</span>
                <span className="summary-value">{subscription.locationCount}</span>
              </div>
            </div>
          </div>

          <div className="billing-actions">
            <button
              className="manage-billing-btn"
              onClick={handleManageBilling}
              disabled={portalLoading}
            >
              {portalLoading ? (
                <>
                  <span className="btn-spinner" />
                  Opening Portal...
                </>
              ) : (
                'Manage Billing'
              )}
            </button>
            <button
              className="change-plan-btn"
              onClick={() => navigate('/plans')}
            >
              Change Plan
            </button>
          </div>
        </>
      ) : (
        <div className="no-subscription">
          <h3>No Active Subscription</h3>
          <p>
            Connect to view your subscription details. Choose a plan to get
            started with BigSystemOne.
          </p>
          <button
            className="no-subscription-cta"
            onClick={() => navigate('/plans')}
          >
            View Plans
          </button>
        </div>
      )}
    </div>
  );
};

export default BillingManagement;
